import { normalizeEntities } from '../lib/entityCategories';

// Extract entity names + full records from the chat's MCP intermediate_steps.
// Driven by tool_name + tool_args (the Input), since several tools
// (search_directory, query_dataset) route to different categories via their
// arguments rather than their name. Full records are kept so detail can be
// rendered directly from the MCP result with no extra API call.

function parse(value) {
  if (value == null) return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
}

function isObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value);
}

function toolKey(toolName) {
  return String(toolName || '').toLowerCase();
}

function stepArgs(step) {
  return step && step.tool_args ? parse(step.tool_args) : {};
}

// ── name keys ──
const ARTIST_NAME_KEYS = ['name', 'n', 'title', 'artist', 'artist_name', 'artistName'];
const EVENT_NAME_KEYS = [
  'name', 'n', 'title', 'event', 'event_name', 'eventName', 'Event',
  'tour', 'Tour', 'festival', 'Festival', 'show_name', 'showName',
];
const VENUE_NAME_KEYS = ['name', 'n', 'title', 'venue', 'venue_name', 'venueName', 'Venue'];

const SHOW_NAME_KEYS = ['artist', 'Artist', 'name', 'n', 'title'];
const SHOW_SLUG_KEYS = ['slug', 'Slug'];

const ARTIST_UUID_KEYS = ['uuid', 'id', 'identifier', 'artist_uuid', 'artistId', 'artist_id'];

function artistUuid(obj) {
  if (!isObject(obj)) return '';
  for (const k of ARTIST_UUID_KEYS) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number') return String(v);
  }
  return '';
}

function collectArtists(value, add, depth = 0) {
  if (depth > 7) return;
  if (Array.isArray(value)) {
    value.forEach((v) => collectArtists(v, add, depth + 1));
    return;
  }
  if (!isObject(value)) return;
  const name = pickName(value, ARTIST_NAME_KEYS);
  if (name) add(name, artistUuid(value));
  for (const [k, v] of Object.entries(value)) {
    if (ARTIST_NAME_KEYS.includes(k) || ARTIST_UUID_KEYS.includes(k)) continue;
    collectArtists(v, add, depth + 1);
  }
}

function pickName(obj, keys) {
  if (!isObject(obj)) return '';
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

// Find the array of records inside a parsed tool result.
function arrayIn(data) {
  if (Array.isArray(data)) return data;
  if (!isObject(data)) return [];
  const arrKeys = ['items', 'events', 'festivals', 'venues', 'results', 'records'];
  for (const k of arrKeys) {
    if (Array.isArray(data[k])) return data[k];
  }
  if (Array.isArray(data.data)) return data.data;
  if (isObject(data.data)) {
    const r = arrayIn(data.data);
    if (r.length) return r;
  }
  for (const k of ['result', 'response', 'payload']) {
    const v = data[k];
    if (Array.isArray(v) || isObject(v)) {
      const r = arrayIn(v);
      if (r.length) return r;
    }
  }
  return [];
}

// ── tool classification ──
function isEventStep(tool, args) {
  const entity = String((args && args.entity) || '').toLowerCase();
  const datasetId = String((args && args.dataset_id) || '').toLowerCase();
  return (
    tool === 'get_events' ||
    tool.includes('soundcharts_search_festival') ||
    tool.includes('soundcharts_search_event') ||
    tool.includes('soundcharts_festival') ||
    tool.includes('soundcharts_artist_events') ||
    (tool.includes('search_directory') && entity === 'event') ||
    (tool.includes('query_dataset') && (datasetId.includes('engagement') || datasetId.includes('boxscore')))
  );
}

function isVenueStep(tool, args) {
  const entity = String((args && args.entity) || '').toLowerCase();
  const datasetId = String((args && args.dataset_id) || '').toLowerCase();
  return (
    tool.includes('soundcharts_search_venue') ||
    tool.includes('soundcharts_venue') ||
    (tool.includes('search_directory') && entity === 'venue') ||
    (tool.includes('query_dataset') && datasetId.includes('venue'))
  );
}

function isShowStep(tool, args) {
  const datasetId = String((args && args.dataset_id) || '').toLowerCase();
  return (
    tool.includes('show') ||
    (tool.includes('query_dataset') && datasetId.includes('show'))
  );
}

// ── extractors ──
export function extractArtists(steps) {
  const out = [];
  const seen = new Set();
  const add = (name, uuid) => {
    const n = String(name || '').trim();
    if (!n || seen.has(n.toLowerCase())) return;
    seen.add(n.toLowerCase());
    out.push({ name: n, uuid: String(uuid || '').trim() });
  };

  for (const step of steps || []) {
    if (step == null || step.tool_result == null) continue;
    const tool = toolKey(step.tool_name);
    const data = parse(step.tool_result);
    const args = stepArgs(step);

    if (tool.includes('soundcharts_search_artist')) {
      const items = (data && (data.data || data).items) || (data && data.artists) || [];
      const term = String((args && args.term) || '').trim().toLowerCase();
      const hit = items.find((i) => String(i.name || '').toLowerCase() === term) || items[0];
      if (hit && hit.name) add(hit.name, artistUuid(hit));
      continue;
    }

    if (
      tool.includes('soundcharts_artist_current_stats') ||
      tool.includes('soundcharts_artist_audience_report') ||
      tool.includes('soundcharts_artist_intel')
    ) {
      const rel = (data && data.data && data.data.related) || (data && data.related);
      if (rel && rel.name) add(rel.name, artistUuid(rel));
      continue;
    }

    if (tool.includes('soundcharts_top_artists') || tool === 'list_artists' || tool === 'dms_summary') {
      collectArtists(data, add);
      continue;
    }
  }

  return out;
}

export function extractEventRecords(steps) {
  const out = [];
  const seen = new Set();
  for (const step of steps || []) {
    if (step == null || step.tool_result == null) continue;
    const tool = toolKey(step.tool_name);
    const args = stepArgs(step);
    if (!isEventStep(tool, args)) continue;
    const data = parse(step.tool_result);
    const rows = arrayIn(data);
    for (const row of rows) {
      if (!isObject(row)) continue;
      const isFestival =
        tool.includes('festival') ||
        (tool.includes('search_directory') && String((args && args.entity) || '').toLowerCase() === 'event') ||
        (row.type === 'festival' || row.Type === 'Festival');
      let name = pickName(row, EVENT_NAME_KEYS);
      if (!name) {
        const artist = pickName(row, ['artist', 'Artist', 'artist_name']);
        const venue = pickName(row, ['venue', 'Venue']);
        name = [artist, venue].filter(Boolean).join(' at ');
      }
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ name, type: isFestival ? 'festival' : 'event', record: row });
    }
  }
  return out;
}

export function extractVenueRecords(steps) {
  const out = [];
  const seen = new Set();
  for (const step of steps || []) {
    if (step == null || step.tool_result == null) continue;
    const tool = toolKey(step.tool_name);
    const args = stepArgs(step);
    if (!isVenueStep(tool, args)) continue;
    const data = parse(step.tool_result);
    const rows = arrayIn(data);
    for (const row of rows) {
      if (!isObject(row)) continue;
      const name = pickName(row, VENUE_NAME_KEYS);
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ name, record: row });
    }
  }
  return out;
}

export function extractShowRecords(steps) {
  const out = [];
  const seen = new Set();
  const push = (slug, name, record) => {
    const key = (slug || name || '').toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push({ name: name || slug || 'Show', slug: slug || '', record });
  };

  for (const step of steps || []) {
    if (step == null || step.tool_result == null) continue;
    const tool = toolKey(step.tool_name);
    const args = stepArgs(step);
    if (!isShowStep(tool, args)) continue;
    const data = parse(step.tool_result);
    if (!isObject(data)) continue;

    // Detail form: { record: { slug, artist: { name } }, ... }
    const rec = data.record;
    if (isObject(rec)) {
      const slug = pickName(rec, SHOW_SLUG_KEYS);
      const artistName = isObject(rec.artist) ? rec.artist.name : rec.artist;
      push(slug, artistName || rec.name || slug, rec);
    }

    // List form: { data: [ { slug, artist, ... } ] }
    const rows = arrayIn(data);
    for (const row of rows) {
      if (!isObject(row)) continue;
      const slug = pickName(row, SHOW_SLUG_KEYS);
      const artist = pickName(row, SHOW_NAME_KEYS);
      push(slug, artist || slug, row);
    }
  }
  return out;
}

export function extractMcpEntities(steps) {
  const empty = {
    entities: normalizeEntities({ artists: [], events: [], venues: [], shows: [] }),
    eventRecords: [],
    venueRecords: [],
  };
  if (!Array.isArray(steps)) return empty;

  const artists = extractArtists(steps);
  const eventRecords = extractEventRecords(steps);
  const venueRecords = extractVenueRecords(steps);
  const showRecords = extractShowRecords(steps);

  const entities = normalizeEntities({
    artists,
    events: eventRecords.map((e) => ({ name: e.name, type: e.type })),
    venues: venueRecords.map((v) => ({ name: v.name })),
    shows: showRecords.map((s) => ({ name: s.name, uuid: s.slug })),
  });

  return { entities, eventRecords, venueRecords };
}
